import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Route as RouteIcon, Clock, Car, ShieldCheck, Fuel, CheckCircle2 } from 'lucide-react';
import { SeoPageLayout, Breadcrumbs, CallCtas } from './SeoPageLayout';
import { useSeo } from '../hooks/useSeo';
import { routePages, formatInr, SITE_URL, PHONE_TEL } from '../data/seoPages';

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="lux-card rounded-2xl p-5" data-testid={testId}>
    <Icon className="w-5 h-5 text-[#d4af37] mb-3" aria-hidden="true" />
    <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-1">{label}</p>
    <p className="text-white font-semibold">{value}</p>
  </div>
);

const RoutePageContent = ({ page }) => {
  const title = `${page.origin} to ${page.destination} Cab – One Way Taxi ${formatInr(page.sedan)} | SG Cabs`;
  const description = `Book a ${page.origin} to ${page.destination} one way cab at a fixed all-inclusive fare: Sedan ${formatInr(page.sedan)}, SUV ${formatInr(page.suv)}. ${page.distance}, ${page.duration}. Tolls, permit, fuel & driver included. 24/7 — call ${PHONE_TEL}.`;
  const path = `/${page.slug}`;
  const reverseSlug = page.reverse ? `bangalore-to-${page.id}-cab` : `${page.id}-to-bangalore-cab`;

  const jsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        name: `${page.origin} to ${page.destination} One Way Cab`,
        serviceType: 'Taxi service',
        provider: { '@id': `${SITE_URL}/#business` },
        areaServed: [{ '@type': 'City', name: page.origin }, { '@type': 'City', name: page.destination }],
        url: `${SITE_URL}${path}`,
        offers: [
          { '@type': 'Offer', name: 'Sedan (Dzire / Etios)', price: String(page.sedan), priceCurrency: 'INR', availability: 'https://schema.org/InStock' },
          { '@type': 'Offer', name: 'SUV (Ertiga / Innova)', price: String(page.suv), priceCurrency: 'INR', availability: 'https://schema.org/InStock' },
        ],
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'One Way Drops', item: `${SITE_URL}/#intercity` },
          { '@type': 'ListItem', position: 3, name: `${page.origin} to ${page.destination} Cab`, item: `${SITE_URL}${path}` },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          { '@type': 'Question', name: `How much does a ${page.origin} to ${page.destination} cab cost?`, acceptedAnswer: { '@type': 'Answer', text: `A one way ${page.origin} to ${page.destination} cab with SG Cabs costs ${formatInr(page.sedan)} for a Sedan and ${formatInr(page.suv)} for an SUV. The fare is all inclusive — tolls, state permit, fuel and driver allowance are covered.` } },
          { '@type': 'Question', name: `How long does it take from ${page.origin} to ${page.destination} by taxi?`, acceptedAnswer: { '@type': 'Answer', text: `The drive is about ${page.distance} and takes ${page.duration} via ${page.highway}, depending on traffic and breaks.` } },
          { '@type': 'Question', name: 'Is the return trip the same price?', acceptedAnswer: { '@type': 'Answer', text: `Yes. ${page.destination} to ${page.origin} is the same fixed one way fare — you pay only for the direction you travel.` } },
        ],
      },
    ],
  }), [page, path]);

  useSeo({ title, description, path, jsonLd });

  return (
    <SeoPageLayout>
      <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid={`route-page-${page.slug}`}>
        <Breadcrumbs items={[{ label: 'One Way Drops', to: '/#intercity' }, { label: `${page.origin} to ${page.destination} Cab` }]} />

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-xs font-semibold uppercase tracking-[0.2em] mb-5">
              One Way Drop · Fixed Fare
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6 leading-[1.1] tracking-tight">
              {page.origin} to {page.destination}
              <span className="block text-[#d4af37]">One Way Cab</span>
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed mb-8 max-w-xl">
              Door-to-door taxi from {page.origin} to {page.destination} with a professional chauffeur. Pay only for one way —
              tolls, state permit, fuel and driver allowance are all included in the fare.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="lux-card rounded-2xl p-6 border-[#d4af37]/40" data-testid="route-fare-sedan">
                <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">Sedan · Dzire / Etios</p>
                <p className="font-display text-3xl font-semibold text-[#d4af37]">{formatInr(page.sedan)}</p>
                <p className="text-xs text-gray-500 mt-1">up to 4 passengers</p>
              </div>
              <div className="lux-card rounded-2xl p-6" data-testid="route-fare-suv">
                <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mb-2">SUV · Ertiga / Innova</p>
                <p className="font-display text-3xl font-semibold text-white">{formatInr(page.suv)}</p>
                <p className="text-xs text-gray-500 mt-1">up to 6–7 passengers</p>
              </div>
            </div>

            <CallCtas message={`Hi SG Cabs, I'd like to book a one way cab from ${page.origin} to ${page.destination}.`} />
          </div>

          <div className="rounded-3xl overflow-hidden lux-card">
            <img src={page.image} alt={`${page.origin} to ${page.destination} cab`} className="w-full h-72 sm:h-96 object-cover" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-16">
          <Stat icon={RouteIcon} label="Distance" value={page.distance} testId="route-stat-distance" />
          <Stat icon={Clock} label="Travel time" value={page.duration} testId="route-stat-duration" />
          <Stat icon={Car} label="Route" value={page.highway} testId="route-stat-highway" />
          <Stat icon={ShieldCheck} label="Availability" value="24/7, any pickup time" testId="route-stat-availability" />
        </div>

        <div className="grid lg:grid-cols-3 gap-10 mt-16">
          <div className="lg:col-span-2 space-y-10">
            <section>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white mb-4">About the {page.origin}–{page.destination} drive</h2>
              <p className="text-gray-400 leading-relaxed">{page.about}</p>
              <p className="text-gray-400 leading-relaxed mt-4">
                The route runs along {page.highway}, passing {page.stops.slice(0, -1).join(', ')} and {page.stops.at(-1)}.
                Your driver will stop for tea, meals and restroom breaks whenever you ask.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white mb-4">What's included in the fare</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {['All tolls on the route', 'Interstate / state permit charges', 'Fuel for the full journey', 'Driver allowance (no night charges)', 'Door-step pickup anywhere in ' + page.origin, 'Clean, sanitised Sedan or SUV'].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" aria-hidden="true" /> {t}
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white mb-4">Frequently asked questions</h2>
              <div className="space-y-4">
                {jsonLd['@graph'][2].mainEntity.map((q) => (
                  <div key={q.name} className="lux-card rounded-2xl p-5">
                    <h3 className="text-white font-semibold mb-2">{q.name}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{q.acceptedAnswer.text}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-6">
            <div className="lux-card rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-[#d4af37] uppercase tracking-[0.15em] mb-4">Return journey</h3>
              <Link to={`/${reverseSlug}`} data-testid="route-reverse-link" className="text-white hover:text-[#d4af37] font-medium">
                {page.destination} to {page.origin} cab →
              </Link>
              <p className="text-xs text-gray-500 mt-2">Same fixed fare in both directions.</p>
            </div>
            <div className="lux-card rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-[#d4af37] uppercase tracking-[0.15em] mb-4">Other one way drops</h3>
              <ul className="space-y-2" data-testid="route-other-links">
                {routePages.filter((p) => !p.reverse && p.id !== page.id).map((p) => (
                  <li key={p.slug}>
                    <Link to={`/${p.slug}`} className="text-gray-300 hover:text-[#d4af37] text-sm">
                      Bangalore to {p.city} cab · from {formatInr(p.sedan)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lux-card rounded-2xl p-6 flex items-start gap-3">
              <Fuel className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-gray-400">Need a round trip instead? Outstation cabs are ₹14/km (Sedan) and ₹18/km (SUV). <Link to="/#fare-calculator" className="text-[#d4af37] hover:underline">Calculate your fare</Link>.</p>
            </div>
          </aside>
        </div>
      </article>
    </SeoPageLayout>
  );
};

export default function RoutePage({ page }) {
  return <RoutePageContent key={page.slug} page={page} />;
}
